// src/views/adminView/contractsView/adminContractsView.jsx
// Empresas — hub del ciclo del comercio sobre los primitivos canónicos
// (PageHeader, Tabs, KpiFilterCards, Stat, Avatar, SectionCard, Card, Modal,
// Button, Badge, KV, Input, Select, EmptyState) + Tailwind.
// Subnav (con deep-link ?tab=): Directorio (datos reales como grid de
// EmpresaCard) | Cuentas | Holdings (ficha del holding con ?holding=). Los
// tabs Ex-clientes, Cumplimiento de cartera y
// Pipeline se retiraron del hub (2026-08-20); el expediente de cumplimiento
// sigue vivo como tab del detalle de empresa y el alta "Como prospecto" del
// header sigue creando prospectos vía API.
// Conserva 1:1 la capa de datos y todas las acciones (búsqueda por nombre,
// razón social o RUT, scroll infinito, filtro de estado server-side,
// bloqueo/desbloqueo por no pago, detalle/edición inline con tabs, CRUD de
// direcciones, rate card).
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Building2,
  Plus,
  Search,
  X,
  ArrowLeft,
  Save,
  Pencil,
  Trash2,
  MapPin,
  Ban,
  LockOpen,
  Clock,
  Link2,
  AlertTriangle,
  Coins,
  CheckCircle2,
  XCircle,
  Package,
  ShieldCheck,
  Users,
  UserPlus,
  LayoutDashboard,
  FileText,
  BellRing,
  Network,
} from "lucide-react";

import {
  getAllContracts,
  getCommunes,
  getOneContract,
  getRegions,
  updateContract,
  togglePaymentBlock,
} from "../../../api/contracts";
import { useCountry } from "../../../context/CountryContext";
import useAuth from "../../../hooks/useAuth";
import { roleRoutes } from "../../../utils/roles";
import { createProspect } from "../../../api/prospects";
import {
  addCompanyShippingAddress,
  deleteCompanyShippingAddress,
  updateCompanyShippingAddress,
} from "../../../api/company";
import { showToast } from "../../../utils/showToast";
import { apiBodyMessage } from "../../../utils/apiErrorMessage";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { cn } from "../../../utils/cn";
import { formatNumberCL } from "../../../utils/formatters";

import { PageHeader } from "../../../components/ui/PageHeader";
import { SectionCard } from "../../../components/ui/SectionCard";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { Avatar } from "../../../components/ui/Avatar";
import { KpiFilterCards } from "../../../components/ui/KpiFilterCards";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { SortControl } from "../../../components/ui/SortControl";
import { Modal } from "../../../components/ui/Modal";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/Tabs";
import { Stat } from "../../../components/ui/Stat";
import CreateContractWizard from "./CreateContractWizard";
import useClientVolume30d from "../../../hooks/useClientVolume30d";
import RateCardEditor from "./RateCardEditor";
import EmpresaCard from "./EmpresaCard";
import { contractMatchesSearch } from "./contractSearch";
import CuentasUsuariosPanel from "./CuentasUsuariosPanel";
import HoldingsPanel from "./holdings/HoldingsPanel";
import HoldingMembershipNotice from "./holdings/HoldingMembershipNotice";
import CumplimientoEmpresaBody from "./compliance/CumplimientoEmpresaBody";
import ContractRenewalSection from "./ContractRenewalSection";
import RenewContractModal from "./RenewContractModal";
import { resumenDeContract } from "./compliance/complianceUtils";
import { readComplianceStored } from "./compliance/useComplianceStore";
import { useCarteraCompliance } from "./compliance/useCarteraCompliance";
import DetalleResumenTab from "./detalle/DetalleResumenTab";
import DetalleKpis from "./detalle/DetalleKpis";
import EntregasHeatmap from "../../../components/deliveryActivity/EntregasHeatmap";
import TarifaMatrixReadOnly from "./detalle/TarifaMatrixReadOnly";
import DetalleIntegracionesTab from "./detalle/DetalleIntegracionesTab";
import CommerceCollectionRuleForm from "../cobro/components/CommerceCollectionRuleForm";
import ProspectFormModal from "../prospects/ProspectFormModal";
import { PHONE_PREFIX, STAGE_ORDER } from "../prospects/prospectsShared";

const LIMIT = 12;

// Valores válidos del subnav del hub (deep-link ?tab=).
const TAB_VALUES = ["directorio", "cuentas", "holdings"];
// Valores válidos de los tabs LOCALES del detalle de empresa.
const TAB_VALUES_DETALLE = ["resumen", "contrato", "tarifas", "cumplimiento", "integraciones", "cobranza"];

// Roles que pueden renovar un contrato: el admin y el superadmin logístico del operador, la
// misma allowlist por `role._id` que OperatorAdminGuard de contracts (soporte y supervisor
// recibirían 403). La autoridad es contracts; esto evita ofrecer una acción que va a fallar.
// También son los únicos que pueden cambiar el RUT de la ficha de la empresa.
const RENEW_ROLE_IDS = new Set([roleRoutes.admin, roleRoutes.superadmin]);

const defaultShippingAddress = {
  address: "",
  municipality: "",
  state: "",
  contactPhone: "",
  type: "BODEGA",
};

// Iniciales (máx 2) del nombre de la empresa para el avatar.
const initialsOf = (n) =>
  ((n || "").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("") || "?").toUpperCase();

const DAY_MS = 24 * 60 * 60 * 1000;

// Clasificación de vigencia del contrato (mismo criterio que DetalleKpis):
// terminada | por_vencer (≤30 días) | vigente | indefinida (sin endDate).
const vigenciaOf = (contract) => {
  const end = contract?.validityPeriod?.endDate;
  if (!end) return "indefinida";
  const endMs = new Date(end).getTime();
  if (endMs < Date.now()) return "terminada";
  if (endMs - Date.now() <= 30 * DAY_MS) return "por_vencer";
  return "vigente";
};

// Cuántas tarjetas se muestran por tanda en el modo client-side (orden/filtros
// sobre la cartera completa); "Mostrar más" avanza de a PAGE_CLIENT.
const PAGE_CLIENT = 24;

// Campo etiquetado para los formularios (label + control + error).
function Field({ label, htmlFor, error, className, children }) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-medium text-fg">
          {label}
        </label>
      )}
      {children}
      {error && <span className="text-xs text-status-error">{error}</span>}
    </div>
  );
}

const ContractsAdmin = () => {
  const { t } = useTranslation();
  const { countryId } = useCountry();
  // `user.role` es el _id del rol del token (AuthProvider), como en el resto de los permisos.
  const { user } = useAuth();
  const canRenew = RENEW_ROLE_IDS.has(user?.role);
  // Volumen de pedidos por comercio (últimos 30 días) — 1 llamada para toda la
  // cartera. Alimenta las tarjetas, el KPI (con alcance del filtro activo) y el
  // ordenamiento por volumen.
  const { volume, loading: volumeLoading, countOf } = useClientVolume30d();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailEditFields, setDetailEditFields] = useState({ pickUpTime: "", status: "" });
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [createWizardOpen, setCreateWizardOpen] = useState(false);
  // Prefill del wizard al convertir un prospecto en cliente (tab Pipeline).
  const [wizardPrefill, setWizardPrefill] = useState(null);

  // Subnav del hub "Empresas" (Directorio | Cuentas) con
  // deep-link ?tab= (patrón de UsersModule). El
  // detalle inline (viewModalOpen) retorna antes que el subnav, así que al
  // abrir una empresa se muestra el detalle, no el hub.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = searchParams.get("tab");
    return TAB_VALUES.includes(fromUrl) ? fromUrl : "directorio";
  });
  const setTab = (tab) => {
    setActiveTab(tab);
    // Functional update: preserva los demás query params (antes se borraban).
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        // Los params del tab Cuentas (detalle inline ?user= y filtro
        // ?empresa=) no sobreviven al cambio de tab: quedarían pegajosos y al
        // volver reabrirían solos el detalle/filtro.
        if (tab !== "cuentas") {
          next.delete("user");
          next.delete("empresa");
        }
        // Lo mismo con la ficha del tab Holdings (?holding=).
        if (tab !== "holdings") next.delete("holding");
        return next;
      },
      { replace: true },
    );
  };

  // "Administrar en Cuentas" desde la ficha o el drawer de cumplimiento:
  // cierra el detalle inline PRIMERO (es estado local — un link a la misma
  // ruta no lo cierra) y aterriza en el tab Cuentas con el filtro de esa
  // empresa aplicado (?empresa=, que lee CuentasUsuariosPanel).
  const goCuentas = (companyId) => {
    setViewModalOpen(false);
    setActiveTab("cuentas");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "cuentas");
        next.delete("user");
        if (companyId) next.set("empresa", companyId);
        else next.delete("empresa");
        return next;
      },
      { replace: true },
    );
  };

  // Sincronización URL→estado en navegaciones posteriores al mount: el ?tab=
  // solo se leía en el useState inicial, así que clickear el ítem de menú
  // estando dentro (/admin/contracts sin ?tab) no volvía a Directorio.
  useEffect(() => {
    const fromUrl = searchParams.get("tab");
    const next = TAB_VALUES.includes(fromUrl) ? fromUrl : "directorio";
    setActiveTab((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  // Dropdown "Sumar empresa" del header (prospecto | cliente).
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef(null);

  // Alta rápida de prospecto desde el header ("Sumar empresa → Como prospecto").
  const [prospectFormOpen, setProspectFormOpen] = useState(false);
  const [prospectSubmitting, setProspectSubmitting] = useState(false);

  // Tabs LOCALES del detalle de empresa (Resumen | Contrato | Tarifas |
  // Cumplimiento | Integraciones).
  const [detailTab, setDetailTab] = useState("resumen");

  // Modal "Renovar contrato" del detalle: lo abren la sección "Contrato firmado y vigencia"
  // (pestaña Contrato) y la fila "Contrato marco" del cumplimiento.
  const [renewOpen, setRenewOpen] = useState(false);

  const [paymentBlockModalOpen, setPaymentBlockModalOpen] = useState(false);
  const [paymentBlockTarget, setPaymentBlockTarget] = useState(null);
  const [paymentBlockReason, setPaymentBlockReason] = useState("");
  const [paymentBlockSaving, setPaymentBlockSaving] = useState(false);

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressModalMode, setAddressModalMode] = useState("create");
  const [addressFormData, setAddressFormData] = useState({ ...defaultShippingAddress });
  const [addressFormErrors, setAddressFormErrors] = useState({});
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressRegions, setAddressRegions] = useState([]);
  const [addressCommunes, setAddressCommunes] = useState([]);
  const [addressLoadingRegions, setAddressLoadingRegions] = useState(false);
  const [addressLoadingCommunes, setAddressLoadingCommunes] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [hasMore, setHasMore] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 400);

  // Filtro por estado (server-side). Lo manejan las KPI cards.
  const [statusFilter, setStatusFilter] = useState("");
  // Quick-filter de bloqueados por no pago. El universo NO es el acumulador del
  // scroll infinito sino la cartera completa (ver blockedCartera más abajo).
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  // Filtro por vigencia del contrato y ordenamiento. Cualquiera de los dos
  // activa el "modo cartera": la lista sale de la cartera COMPLETA (client-side,
  // mismo patrón que onlyBlocked) porque la paginación del servidor no sabe
  // ordenar ni filtrar por vigencia.
  const [vigenciaFilter, setVigenciaFilter] = useState("");
  const [sortField, setSortField] = useState(""); // "" | orders | name | expiry
  const [sortDir, setSortDir] = useState("desc"); // dirección del criterio activo
  // Ventana de tarjetas visibles en modo cartera ("Mostrar más").
  const [clientVisibleCount, setClientVisibleCount] = useState(PAGE_CLIENT);

  // Conteos por estado para las KPI cards (server-side, independientes del scroll).
  const [statusCounts, setStatusCounts] = useState({ all: 0, active: 0, inactive: 0, pending: 0 });

  const observerTarget = useRef(null);
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(loadingMore);
  const loadingRef = useRef(loading);
  // Página vigente como ref, no como state: antes el observer la leía dentro
  // de un updater de setState y disparaba el fetch AHÍ dentro; en StrictMode el
  // updater corre dos veces → dos fetch de la misma página. Nadie la renderiza.
  const currentPageRef = useRef(1);
  // Páginas ya pedidas en la serie de paginación vigente. Guard SÍNCRONO: los
  // refs de loading se sincronizan en un efecto, o sea un tick TARDE, así que
  // dos intersecciones seguidas pasaban ambas.
  const requestedPagesRef = useRef(new Set([1]));

  useEffect(() => {
    hasMoreRef.current = hasMore;
    loadingMoreRef.current = loadingMore;
    loadingRef.current = loading;
  }, [hasMore, loadingMore, loading]);

  // Búsqueda en vivo (debounced) por nombre, razón social, RUT o dirección fiscal.
  useEffect(() => {
    setSearchTerm(debouncedSearch.trim());
  }, [debouncedSearch]);

  // Cierre del dropdown "Sumar empresa": click fuera + Escape. Vive en el
  // header (sin overflow clipping), así que no necesita portal.
  useEffect(() => {
    if (!addMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setAddMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setAddMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [addMenuOpen]);

  // Semáforo de cumplimiento agregado (front-only, localStorage) sobre la
  // cartera COMPLETA (hook compartido: fetch paginado una vez por montaje del
  // hub + reactividad a cada write del store vía useComplianceVersion). Lo
  // consume el Stat "Carpeta al día" del Directorio. El version bump además
  // re-renderiza este componente, así el badge del tab Cumplimiento del
  // DETALLE (rCumplimiento) recalcula en vivo tras Aprobar/Subir/Rechazar
  // dentro de CumplimientoEmpresaBody.
  const carteraCompliance = useCarteraCompliance();
  const { cartera } = carteraCompliance;

  // Universo del chip/filtro "Bloqueados" = cartera COMPLETA. Reutiliza el
  // fetch paginado que useCarteraCompliance ya hace una vez por montaje del
  // hub (nada de un tercer fetch). Antes se contaba sobre `contracts` (el
  // acumulador del scroll infinito) → 0 al cargar y 6 tras scrollear, cuando
  // la cartera tiene 18.
  const carteraContracts = carteraCompliance.contracts;
  const blockedCartera = useMemo(
    () => carteraContracts.filter((c) => c.paymentBlock?.isBlocked),
    [carteraContracts],
  );
  // Modo cartera: con bloqueados, vigencia u orden activos la lista sale de la
  // cartera COMPLETA. La búsqueda y el estado —server-side en la lista
  // paginada— se aplican acá client-side para que el filtrado sea coherente.
  const clientSideMode = onlyBlocked || vigenciaFilter !== "" || sortField !== "";
  const carteraFiltered = useMemo(() => {
    let list = carteraContracts.filter((c) => {
      if (onlyBlocked && !c.paymentBlock?.isBlocked) return false;
      if (statusFilter && c.status !== statusFilter) return false;
      if (vigenciaFilter && vigenciaOf(c) !== vigenciaFilter) return false;
      // Mismo criterio que la búsqueda server-side de la lista paginada (ver
      // contractSearch.js): el KPI y la lista no pueden contradecirse.
      return contractMatchesSearch(c, searchTerm);
    });
    if (sortField) {
      const asc = sortDir === "asc";
      const name = (c) => (c.companyId?.name || "").toLowerCase();
      // Sin dato de volumen (empresa sin companyId poblado) → al FINAL en ambos
      // sentidos: -1 en desc, Infinity en asc.
      const orders = (c) => countOf(c.companyId?._id) ?? (asc ? Infinity : -1);
      // Vencimiento asc = "más próximo primero": FUTUROS por fecha ascendente,
      // los ya terminados después (no contaminan el tope) y los indefinidos al
      // final. Desc = "más lejano primero": fecha descendente pura (el orden
      // recorre futuro lejano → recién vencidos), indefinidos igual al final.
      const expiryRank = (c) => {
        const end = c.validityPeriod?.endDate;
        if (!end) return [2, 0];
        const t = new Date(end).getTime();
        if (!asc) return [0, -t];
        return t >= Date.now() ? [0, t] : [1, t];
      };
      const cmp = {
        name: (a, b) => (asc ? name(a).localeCompare(name(b)) : name(b).localeCompare(name(a))),
        orders: (a, b) => (asc ? orders(a) - orders(b) : orders(b) - orders(a)),
        expiry: (a, b) => {
          const [ra, ta] = expiryRank(a);
          const [rb, tb] = expiryRank(b);
          return ra !== rb ? ra - rb : ta - tb;
        },
      }[sortField];
      if (cmp) list = [...list].sort(cmp);
    }
    return list;
  }, [carteraContracts, onlyBlocked, statusFilter, vigenciaFilter, searchTerm, sortField, sortDir, countOf]);

  // Al cambiar cualquier filtro/orden la ventana de "Mostrar más" se reinicia.
  useEffect(() => {
    setClientVisibleCount(PAGE_CLIENT);
  }, [onlyBlocked, statusFilter, vigenciaFilter, searchTerm, sortField, sortDir]);

  // KPI "Pedidos últimos 30 días" con ALCANCE del filtro activo: sin filtros es
  // el total de la cartera (todas las filas de client-volume); con filtros es
  // la suma de las empresas que matchean (cartera completa, no solo la ventana
  // visible). Empresas sin fila en client-volume son cero real. Vive ACÁ y no
  // junto al render: más abajo hay early-returns (wizard/detalle) y un hook
  // después de ellos viola rules-of-hooks.
  const hasScopeFilters = !!(statusFilter || onlyBlocked || vigenciaFilter || searchTerm.trim());
  const orders30dStat = useMemo(() => {
    if (volumeLoading || (hasScopeFilters && carteraCompliance.loading)) {
      return { value: "…", hint: null };
    }
    if (!volume) return { value: "—", hint: t("No disponible") };
    // Con filtros la suma sale de la cartera: si ese fetch falló, un 0 sería
    // dato falso.
    if (hasScopeFilters && carteraCompliance.error) {
      return { value: "—", hint: t("No disponible") };
    }
    if (!hasScopeFilters) {
      return { value: formatNumberCL(volume.total), hint: t("Creados en la cartera completa") };
    }
    const sum = carteraFiltered.reduce((s, c) => s + (countOf(c.companyId?._id) || 0), 0);
    return {
      value: formatNumberCL(sum),
      hint: t("Suma del filtro actual · {{n}} empresas", { n: carteraFiltered.length }),
    };
  }, [volume, volumeLoading, hasScopeFilters, carteraFiltered, carteraCompliance.loading, carteraCompliance.error, countOf, t]);

  const fetchContracts = useCallback(
    async (reset = false, pageToFetch = null) => {
      const page = pageToFetch || (reset ? 1 : currentPageRef.current);

      if (reset) {
        loadingRef.current = true;
        setLoading(true);
        setContracts([]);
        currentPageRef.current = 1;
        requestedPagesRef.current = new Set([1]);
        setHasMore(true);
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const response = await getAllContracts({
          page,
          limit: LIMIT,
          businessIdentifier: searchTerm,
          status: statusFilter || undefined,
        });

        if (response.error) {
          // Liberamos la página para que un reintento del observer pueda pedirla.
          requestedPagesRef.current.delete(page);
          // El 400 de negocio del microservicio (usuario sin logisticCompany,
          // id de empresa logística inválido…) trae el motivo en el body Nest:
          // mostrarlo en vez del texto genérico. Sin texto útil (500, HTML de
          // proxy) cae al genérico de siempre.
          showToast(apiBodyMessage(response.error) ?? t("Error al cargar contratos"), false);
          setLoading(false);
          setLoadingMore(false);
          setHasMore(false);
          return;
        }

        const newContracts = response.docs || [];
        const pages = response.totalPages || 1;

        if (reset) {
          setContracts(newContracts);
          currentPageRef.current = 1;
        } else {
          // Dedup por _id al acumular: la API puede repetir docs entre páginas
          // (orden no estable) y cualquier re-pedido reinyectaba la misma
          // página → 348 tarjetas para 180 empresas y 3.444 warnings de key.
          // El updater es puro (reconstruye `seen` desde `prev`), así que
          // tolera el doble invoke de StrictMode.
          setContracts((prev) => {
            const seen = new Set(prev.map((c) => c._id));
            const fresh = [];
            newContracts.forEach((c) => {
              if (!c?._id || seen.has(c._id)) return;
              seen.add(c._id);
              fresh.push(c);
            });
            return fresh.length ? [...prev, ...fresh] : prev;
          });
          currentPageRef.current = page;
        }

        setHasMore(page < pages);
      } catch (error) {
        requestedPagesRef.current.delete(page);
        console.error("Error fetching contracts:", error);
        showToast(t("Error al cargar contratos"), false);
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchTerm, statusFilter],
  );

  useEffect(() => {
    // En modo cartera la lista paginada no está a la vista: no la refetcheamos
    // por cada cambio de filtro (5 requests por click). Al salir del modo,
    // clientSideMode vuelve a false y este efecto re-sincroniza.
    if (clientSideMode) return;
    fetchContracts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, clientSideMode]);

  // Conteos por estado para las KPI cards: 1 request liviano (limit:1) por
  // estado, leyendo el total (totalDocs si viene, si no totalPages que con
  // limit:1 == total). Cuelga de searchTerm (ya debounced), en su propio state.
  const fetchStatusCounts = useCallback(async () => {
    const base = { page: 1, limit: 1, businessIdentifier: searchTerm };
    const read = (r) => (r?.error ? 0 : (r.totalDocs ?? r.totalPages ?? 0));
    try {
      const [all, active, inactive, pending] = await Promise.all([
        getAllContracts(base),
        getAllContracts({ ...base, status: "active" }),
        getAllContracts({ ...base, status: "inactive" }),
        getAllContracts({ ...base, status: "pending" }),
      ]);
      return {
        all: read(all),
        active: read(active),
        inactive: read(inactive),
        pending: read(pending),
      };
    } catch {
      return null;
    }
  }, [searchTerm]);

  useEffect(() => {
    let on = true;
    fetchStatusCounts().then((c) => {
      if (on && c) setStatusCounts(c);
    });
    return () => {
      on = false;
    };
  }, [fetchStatusCounts]);

  // Refresca lista + conteos tras una mutación de estado.
  const refreshAll = async () => {
    fetchContracts(true);
    const c = await fetchStatusCounts();
    if (c) setStatusCounts(c);
  };

  // Scroll infinito.
  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return undefined;

    const handleIntersect = (entries) => {
      const entry = entries[0];
      if (!entry.isIntersecting) return;
      if (!hasMoreRef.current || loadingMoreRef.current || loadingRef.current) return;

      const nextPage = currentPageRef.current + 1;
      // Guard idempotente: una página se pide UNA vez por serie de paginación.
      if (requestedPagesRef.current.has(nextPage)) return;
      requestedPagesRef.current.add(nextPage);
      currentPageRef.current = nextPage;
      fetchContracts(false, nextPage);
    };

    const observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.1,
      rootMargin: "200px",
    });

    if (hasMore && !loadingMore && !loading) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
    // activeTab: al volver al tab Directorio el nodo observado se re-monta, así
    // que re-corremos el efecto para re-adjuntar el observer al nodo vigente.
  }, [hasMore, loadingMore, loading, fetchContracts, activeTab]);

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  // Abre el detalle inline. `tab` permite aterrizar en un tab específico del
  // detalle (p.ej. la cartera de Cumplimiento abre directo su expediente).
  const handleViewClick = (contract, tab = "resumen") => {
    setDetailTab(TAB_VALUES_DETALLE.includes(tab) ? tab : "resumen");
    setRenewOpen(false);
    setSelectedContract(contract);
    setViewModalOpen(true);
  };

  const handleCloseViewDetail = () => {
    setViewModalOpen(false);
    setRenewOpen(false);
    setSelectedContract(null);
    setDetailTab("resumen");
  };

  // Alta de prospecto desde el header. Mismo payload que ModernAdminProspects:
  // campos vacíos como undefined, phone omitido si es solo el prefijo.
  const handleProspectSubmit = async (values) => {
    setProspectSubmitting(true);
    const cleaned = {
      fullName: values.fullName?.trim(),
      companyName: values.companyName?.trim(),
      phone:
        values.phone?.trim() && values.phone.trim() !== PHONE_PREFIX.trim()
          ? values.phone.trim()
          : undefined,
      email: values.email?.trim() || undefined,
      industry: values.industry?.trim() || undefined,
      website: values.website?.trim() || undefined,
      notes: values.notes?.trim() || undefined,
      businessIdentifier: values.businessIdentifier?.trim() || undefined,
      stage: STAGE_ORDER.includes(values.stage) ? values.stage : "nuevo",
      blocker: values.blocker?.trim() || undefined,
    };

    const response = await createProspect({ values: cleaned });
    setProspectSubmitting(false);

    if (response?.error) {
      const msg = Array.isArray(response.error?.message)
        ? response.error.message.join(", ")
        : response.error?.message || t("Error al guardar el prospecto");
      showToast(msg, false);
      return;
    }

    showToast(response?.data?.message || t("Prospecto creado correctamente"), true);
    setProspectFormOpen(false);
  };

  useEffect(() => {
    if (selectedContract) {
      setDetailEditFields({
        pickUpTime: selectedContract.companyId?.pickUpTime || "",
        status: selectedContract.status || "",
      });
    }
  }, [selectedContract]);

  const hasDetailChanges =
    selectedContract &&
    (detailEditFields.pickUpTime !== (selectedContract.companyId?.pickUpTime || "") ||
      detailEditFields.status !== (selectedContract.status || ""));

  const handleSaveDetails = async () => {
    setSaving(true);
    const response = await updateContract(selectedContract._id, {
      status: detailEditFields.status,
      pickUpTime: detailEditFields.pickUpTime,
    });
    if (response.error) {
      showToast(t("Error al actualizar el contrato"), false);
    } else {
      showToast(t("Contrato actualizado exitosamente"), true);
      await refreshSelectedContract();
      refreshAll();
    }
    setSaving(false);
  };

  const handlePaymentBlockClick = (contract, e) => {
    if (e) e.stopPropagation();
    setPaymentBlockTarget(contract);
    setPaymentBlockReason("");
    setPaymentBlockModalOpen(true);
  };

  const handlePaymentBlockConfirm = async () => {
    if (!paymentBlockTarget) return;
    setPaymentBlockSaving(true);

    const willBlock = !paymentBlockTarget.paymentBlock?.isBlocked;
    const response = await togglePaymentBlock(paymentBlockTarget._id, {
      isBlocked: willBlock,
      reason: paymentBlockReason || undefined,
    });

    if (response.error) {
      showToast(willBlock ? t("Error al bloquear el contrato") : t("Error al desbloquear el contrato"), false);
    } else {
      showToast(
        willBlock
          ? `${t("Empresa")} ${paymentBlockTarget.companyId?.name || ""} ${t("bloqueada por no pago")}`
          : `${t("Empresa")} ${paymentBlockTarget.companyId?.name || ""} ${t("desbloqueada exitosamente")}`,
        true,
      );
      refreshAll();
      // Si el detalle abierto es el afectado, refrescarlo también.
      if (selectedContract?._id === paymentBlockTarget._id) {
        refreshSelectedContract();
      }
      setPaymentBlockModalOpen(false);
      setPaymentBlockTarget(null);
    }
    setPaymentBlockSaving(false);
  };

  // El país puede resolver después del primer render (default → país real de la
  // empresa); invalida el catálogo cacheado para que el próximo modal recargue
  // con el país correcto en vez de conservar regiones del país anterior.
  useEffect(() => {
    setAddressRegions([]);
    setAddressCommunes([]);
  }, [countryId]);

  const ensureRegionsLoaded = async () => {
    if (addressRegions.length > 0 || addressLoadingRegions || !countryId) return;
    setAddressLoadingRegions(true);
    const data = await getRegions({ countryId });
    if (!data.error) {
      setAddressRegions(data.map((region) => ({ value: region.code, label: region.name })));
    } else {
      showToast(t("No se pudieron cargar las regiones"), false);
    }
    setAddressLoadingRegions(false);
  };

  const loadCommunes = async (regionCode) => {
    if (!regionCode || !countryId) {
      setAddressCommunes([]);
      return;
    }
    setAddressLoadingCommunes(true);
    const data = await getCommunes({ countryId, regionId: regionCode });
    if (!data.error) {
      setAddressCommunes(data.map((commune) => ({ value: commune.name, label: commune.name })));
    } else {
      setAddressCommunes([]);
      showToast(t("No se pudieron cargar las comunas"), false);
    }
    setAddressLoadingCommunes(false);
  };

  const openAddAddressModal = async () => {
    setAddressModalMode("create");
    setSelectedAddressId(null);
    setAddressFormData({ ...defaultShippingAddress });
    setAddressFormErrors({});
    setAddressCommunes([]);
    await ensureRegionsLoaded();
    setAddressModalOpen(true);
  };

  const openEditAddressModal = async (address) => {
    setAddressModalMode("edit");
    setSelectedAddressId(address?._id || null);
    setAddressFormData({
      address: address?.address || "",
      municipality: address?.municipality || "",
      state: address?.state || "",
      contactPhone: address?.contactPhone || "",
      type: address?.type || "BODEGA",
    });
    setAddressFormErrors({});
    await ensureRegionsLoaded();
    if (address?.state) {
      await loadCommunes(address.state);
    } else {
      setAddressCommunes([]);
    }
    setAddressModalOpen(true);
  };

  const handleAddressFieldChange = (field, value) => {
    setAddressFormData((prev) => ({ ...prev, [field]: value }));
    if (addressFormErrors[field]) {
      setAddressFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleAddressRegionChange = async (regionCode) => {
    setAddressFormData((prev) => ({ ...prev, state: regionCode, municipality: "" }));
    setAddressFormErrors((prev) => ({ ...prev, state: null, municipality: null }));
    await loadCommunes(regionCode);
  };

  const validateAddressForm = () => {
    const newErrors = {};
    if (!addressFormData.address.trim()) newErrors.address = t("La dirección es requerida");
    if (!addressFormData.state) newErrors.state = t("La región es requerida");
    if (!addressFormData.municipality) newErrors.municipality = t("La comuna es requerida");
    if (!addressFormData.contactPhone.trim()) newErrors.contactPhone = t("El teléfono de contacto es requerido");
    if (!addressFormData.type) newErrors.type = t("El tipo es requerido");
    setAddressFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const refreshSelectedContract = async () => {
    if (!selectedContract?._id) return;
    const refreshed = await getOneContract(selectedContract._id);
    if (refreshed?.error) {
      showToast(t("No se pudo actualizar el contrato"), false);
      return;
    }
    setSelectedContract(refreshed);
    setContracts((prev) => prev.map((contract) => (contract._id === refreshed._id ? refreshed : contract)));
  };

  // Tras renovar: el detalle se relee poblado (NUNCA con la respuesta de la renovación, que
  // no trae el contrato) y se recargan la lista, los conteos y la cartera completa, de la que
  // salen la vigencia, el orden por vencimiento y el semáforo de cumplimiento del hub.
  const handleContractRenewed = async () => {
    setRenewOpen(false);
    await refreshSelectedContract();
    refreshAll();
    carteraCompliance.reload();
  };

  const handleSaveAddress = async () => {
    const companyId = selectedContract?.companyId?._id;
    if (!companyId) {
      showToast(t("No se encontró la empresa asociada al contrato"), false);
      return;
    }
    if (!validateAddressForm()) return;

    setAddressSaving(true);
    let response;
    if (addressModalMode === "create") {
      response = await addCompanyShippingAddress(companyId, addressFormData);
    } else {
      if (!selectedAddressId) {
        showToast(t("No se encontró el ID de la dirección"), false);
        setAddressSaving(false);
        return;
      }
      response = await updateCompanyShippingAddress(companyId, selectedAddressId, addressFormData);
    }

    if (response?.error) {
      showToast(t("No se pudo guardar la dirección"), false);
    } else {
      showToast(addressModalMode === "create" ? t("Dirección agregada") : t("Dirección actualizada"), true);
      setAddressModalOpen(false);
      await refreshSelectedContract();
    }
    setAddressSaving(false);
  };

  const handleDeleteAddress = async (address) => {
    const companyId = selectedContract?.companyId?._id;
    const addressId = address?._id;
    if (!companyId) {
      showToast(t("No se encontró la empresa asociada al contrato"), false);
      return;
    }
    if (!addressId) {
      showToast(t("No se encontró el ID de la dirección"), false);
      return;
    }
    const confirmed = window.confirm(t("¿Estás seguro de eliminar esta dirección?"));
    if (!confirmed) return;
    setAddressSaving(true);
    const response = await deleteCompanyShippingAddress(companyId, addressId);
    if (response?.error) {
      showToast(t("No se pudo eliminar la dirección"), false);
    } else {
      showToast(t("Dirección eliminada"), true);
      await refreshSelectedContract();
    }
    setAddressSaving(false);
  };

  // ── Helpers de presentación ────────────────────────────────────────────
  const statusBadge = (status) => {
    const map = {
      active: { tone: "success", label: t("Activo") },
      inactive: { tone: "neutral", label: t("Inactivo") },
      pending: { tone: "warning", label: t("Pendiente") },
    };
    const cfg = map[status] || map.inactive;
    return (
      <Badge tone={cfg.tone} dot>
        {cfg.label}
      </Badge>
    );
  };

  const paymentBlockBadge = (contract) => {
    if (!contract.paymentBlock?.isBlocked) return null;
    return (
      <Badge tone="error">
        <Ban className="w-3 h-3" aria-hidden="true" />
        {t("Bloqueado")}
      </Badge>
    );
  };

  const BlockButton = ({ contract, className }) => {
    const isBlocked = contract.paymentBlock?.isBlocked;
    return (
      <Button
        variant="outline"
        size="sm"
        className={cn(
          isBlocked
            ? "border-status-success/40 text-status-success hover:bg-status-success-soft"
            : "border-status-warning/40 text-status-warning hover:bg-status-warning-soft",
          className,
        )}
        onClick={(e) => handlePaymentBlockClick(contract, e)}
        iconLeft={isBlocked ? <LockOpen className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
        aria-label={
          isBlocked
            ? `${t("Desbloquear empresa")} ${contract.companyId?.name || ""}`
            : `${t("Bloquear empresa")} ${contract.companyId?.name || ""} ${t("por no pago")}`
        }
      >
        {isBlocked ? t("Desbloquear") : t("Bloquear")}
      </Button>
    );
  };

  // ── Vista: Wizard de creación (reemplaza la lista, inline) ──────────────
  if (createWizardOpen) {
    return (
      <div className="w-full min-w-0 anim-fade-in-up">
        <CreateContractWizard
          inline
          isOpen={createWizardOpen}
          prefill={wizardPrefill}
          toggle={() => {
            setCreateWizardOpen(false);
            setWizardPrefill(null);
          }}
          onSuccess={() => {
            setCreateWizardOpen(false);
            if (wizardPrefill) {
              // Conversión desde Pipeline: el vínculo prospecto→contrato no
              // persiste en backend — la etapa del prospecto no cambia sola.
              showToast(
                t(
                  "Contrato creado. Recuerda mover la etapa del prospecto a mano.",
                ),
                true,
              );
            }
            setWizardPrefill(null);
            refreshAll();
          }}
        />
      </div>
    );
  }

  // ── Vista: Detalle / edición inline (reemplaza la lista) ────────────────
  if (viewModalOpen && selectedContract) {
    const company = selectedContract.companyId || {};
    const addresses = company.shippingAddresses || [];
    const integrations = selectedContract.integrations || [];
    const blocked = selectedContract.paymentBlock?.isBlocked;

    // Badges de los triggers del detalle (cálculo puro por render, sin hooks).
    const pricing = selectedContract.pricing;
    const tarifasCount = (pricing?.zonePricing?.length || 0) + (pricing?.rules?.length || 0);
    const rCumplimiento = resumenDeContract(selectedContract, readComplianceStored(selectedContract._id));
    // 'por_vencer' cuenta como conforme en la barra X/Y, así que con ese estado
    // "obligatorios - conformes" daría 0 en un badge warning: ahí el número
    // correcto es cuántos docs están por vencer o vencidos (no bloqueantes).
    const cumplimientoPendientes =
      rCumplimiento.estado === "por_vencer"
        ? rCumplimiento.partes.porVencer.length + rCumplimiento.partes.vencidos.length
        : rCumplimiento.obligatorios - rCumplimiento.conformes;

    return (
      <>
        <div className="w-full min-w-0 space-y-4 anim-fade-in-up">
          {/* Banda de identidad */}
          <div className="rounded-lg border border-border bg-bg p-4 shadow-card">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <Button variant="ghost" size="sm" onClick={handleCloseViewDetail} iconLeft={<ArrowLeft className="w-4 h-4" />}>
                  {t("Volver")}
                </Button>
                <div className="h-6 w-px bg-border shrink-0" aria-hidden="true" />
                <Avatar size="lg" tone={blocked ? "warning" : "brand"} initials={initialsOf(company.name)} alt={company.name} />
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-fg truncate">{company.name || t("Sin nombre")}</h1>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
                    <span className="font-mono">{company.businessIdentifier || t("Sin RUT")}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                      {company.pickUpTime || "—"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                      {addresses.length} {addresses.length === 1 ? t("dirección") : t("direcciones")}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Link2 className="w-3.5 h-3.5" aria-hidden="true" />
                      {integrations.length} {t("integraciones")}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {statusBadge(selectedContract.status)}
                {paymentBlockBadge(selectedContract)}
                <BlockButton contract={selectedContract} />
              </div>
            </div>
          </div>

          {/* Tabs del detalle: Resumen | Contrato | Tarifas | Cumplimiento | Integraciones */}
          <Tabs value={detailTab} onValueChange={setDetailTab}>
            <TabsList ariaLabel={t("Secciones de la empresa")}>
              <TabsTrigger value="resumen" icon={<LayoutDashboard />}>
                {t("Resumen")}
              </TabsTrigger>
              <TabsTrigger value="contrato" icon={<FileText />}>
                {t("Contrato")}
              </TabsTrigger>
              <TabsTrigger value="tarifas" icon={<Coins />}>
                {t("Tarifas")}
                {tarifasCount > 0 && (
                  <Badge tone="neutral" className="ml-1.5 tabular-nums">
                    {tarifasCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="cumplimiento" icon={<ShieldCheck />}>
                {t("Cumplimiento")}
                {rCumplimiento.estado !== "al_dia" && cumplimientoPendientes > 0 && (
                  <Badge
                    tone={rCumplimiento.estado === "bloqueado" ? "error" : "warning"}
                    className="ml-1.5 tabular-nums"
                  >
                    {cumplimientoPendientes}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="integraciones" icon={<Link2 />}>
                {t("Integraciones")}
              </TabsTrigger>
              <TabsTrigger value="cobranza" icon={<BellRing />}>
                {t("Cobranza")}
              </TabsTrigger>
            </TabsList>

            {/* ── RESUMEN ── KPIs + actividad de entregas + contacto/equipo/condiciones ── */}
            <TabsContent value="resumen" className="mt-4 space-y-4">
              <DetalleKpis contract={selectedContract} />
              <EntregasHeatmap companyId={selectedContract.companyId?._id} />
              <DetalleResumenTab
                contract={selectedContract}
                onGoCuentas={goCuentas}
                canEditTaxId={canRenew}
                onProfileSaved={refreshSelectedContract}
              />
            </TabsContent>

            {/* ── CONTRATO ── parámetros editables + CRUD de direcciones ── */}
            <TabsContent value="contrato" className="mt-4 space-y-4">
              {/* Parámetros editables */}
              <SectionCard
                tone="bg"
                icon={<Building2 className="w-4 h-4" />}
                title={t("Parámetros del contrato")}
                subtitle={t("Hora de retiro y estado del contrato.")}
                bodyClassName="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl"
                action={
                  hasDetailChanges ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setDetailEditFields({
                            pickUpTime: company.pickUpTime || "",
                            status: selectedContract.status || "",
                          })
                        }
                        disabled={saving}
                      >
                        {t("Descartar")}
                      </Button>
                      <Button size="sm" onClick={handleSaveDetails} loading={saving} iconLeft={!saving && <Save className="w-4 h-4" />}>
                        {t("Guardar")}
                      </Button>
                    </div>
                  ) : null
                }
              >
                <Field label={t("Hora de retiro")} htmlFor="detail-pickup">
                  <Input
                    id="detail-pickup"
                    type="time"
                    value={detailEditFields.pickUpTime}
                    onChange={(e) => setDetailEditFields((prev) => ({ ...prev, pickUpTime: e.target.value }))}
                    disabled={saving}
                  />
                </Field>
                <Field label={t("Estado del contrato")} htmlFor="detail-status">
                  <Select
                    id="detail-status"
                    value={detailEditFields.status}
                    onChange={(e) => setDetailEditFields((prev) => ({ ...prev, status: e.target.value }))}
                    disabled={saving}
                    options={[
                      { value: "active", label: t("Activo") },
                      { value: "inactive", label: t("Inactivo") },
                      { value: "pending", label: t("Pendiente") },
                    ]}
                  />
                </Field>
              </SectionCard>

              {/* Contrato firmado y vigencia: renovación + historial de PDFs firmados */}
              <ContractRenewalSection
                contract={selectedContract}
                canRenew={canRenew}
                onRenew={() => setRenewOpen(true)}
              />

              {/* Direcciones de envío */}
              <SectionCard
                tone="bg"
                icon={<MapPin className="w-4 h-4" />}
                title={t("Direcciones de Envío")}
                action={
                  <Button
                    size="sm"
                    onClick={openAddAddressModal}
                    disabled={!company._id || addressSaving}
                    iconLeft={<Plus className="w-4 h-4" />}
                  >
                    {t("Agregar")}
                  </Button>
                }
              >
                {addresses.length > 0 ? (
                  <ul className="divide-y divide-border rounded-lg border border-border bg-bg">
                    {addresses.map((addr, index) => (
                      <li key={addr?._id || index} className="flex items-start gap-3 p-3">
                        <MapPin className="w-4 h-4 text-fg-muted shrink-0 mt-0.5" aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-fg">
                            <span className="font-medium">{addr.address}</span>
                            {addr.municipality ? `, ${addr.municipality}` : ""}
                            {addr.state ? `, ${addr.state}` : ""}
                          </p>
                          <div className="mt-1 flex items-center gap-2 text-xs text-fg-muted">
                            <Badge tone="neutral">{addr.type}</Badge>
                            <span>· {addr.contactPhone}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditAddressModal(addr)}
                            disabled={!addr?._id || addressSaving}
                            iconLeft={<Pencil className="w-4 h-4" />}
                            aria-label={t("Editar")}
                          >
                            <span className="hidden sm:inline">{t("Editar")}</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-status-error hover:bg-status-error-soft"
                            onClick={() => handleDeleteAddress(addr)}
                            disabled={!addr?._id || addressSaving}
                            iconLeft={<Trash2 className="w-4 h-4" />}
                            aria-label={t("Eliminar")}
                          >
                            <span className="hidden sm:inline">{t("Eliminar")}</span>
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="rounded-lg border border-dashed border-border px-4 py-4 text-center text-sm text-fg-muted">
                    {t("No hay direcciones")}
                  </p>
                )}
              </SectionCard>

            </TabsContent>

            {/* ── TARIFAS ── editor existente + matriz de lectura ── */}
            <TabsContent value="tarifas" className="mt-4 space-y-4">
              <SectionCard tone="bg" icon={<Coins className="w-4 h-4" />} title={t("Tarifas de cobro")}>
                {/* Una empresa de un holding con cobro consolidado se cobra con la tarifa del
                    holding: esta solo cotiza en su tienda. */}
                <HoldingMembershipNotice companyId={selectedContract.companyId?._id} />
                <RateCardEditor contract={selectedContract} onSaved={refreshSelectedContract} />
              </SectionCard>
              <TarifaMatrixReadOnly pricing={pricing} pricingVersion={selectedContract.pricingVersion} />
            </TabsContent>

            {/* ── CUMPLIMIENTO ── expediente documental front-only (localStorage) ── */}
            <TabsContent value="cumplimiento" className="mt-4">
              <CumplimientoEmpresaBody
                contract={selectedContract}
                onToast={(msg) => showToast(msg, true)}
                onGoCuentas={goCuentas}
                onRenewContract={canRenew ? () => setRenewOpen(true) : undefined}
              />
            </TabsContent>

            {/* ── INTEGRACIONES ── reales del contrato + catálogo pendiente ── */}
            <TabsContent value="integraciones" className="mt-4">
              <DetalleIntegracionesTab contract={selectedContract} />
            </TabsContent>

            {/* ── COBRANZA ── regla de cobranza del comercio (billing): plazo, pausa y contactos ── */}
            <TabsContent value="cobranza" className="mt-4">
              <SectionCard
                tone="bg"
                icon={<BellRing className="w-4 h-4" />}
                title={t("Cobranza")}
                subtitle={t("Plazo de vencimiento, recordatorios automáticos y contactos de cobranza del comercio.")}
              >
                {selectedContract.companyId?._id ? (
                  <>
                    {/* Una empresa de un holding con cobro consolidado se cobra en la nota del
                        holding, con su plazo y sus contactos: esta regla no la rige. */}
                    <HoldingMembershipNotice companyId={selectedContract.companyId._id} topic="collection" />
                    <CommerceCollectionRuleForm companyId={selectedContract.companyId._id} />
                  </>
                ) : (
                  <p className="text-sm text-fg-muted">{t("Esta empresa no tiene un comercio asociado.")}</p>
                )}
              </SectionCard>
            </TabsContent>
          </Tabs>
        </div>

        {addressModal()}
        {paymentBlockModal()}
        {/* Se monta al abrir: el formulario arranca con el contrato de ese momento. */}
        {renewOpen && (
          <RenewContractModal
            open
            onClose={() => setRenewOpen(false)}
            contract={selectedContract}
            onRenewed={handleContractRenewed}
          />
        )}
      </>
    );
  }

  // ── Vista: Lista (default) ──────────────────────────────────────────────
  function addressModal() {
    const typeOptions = [
      { value: "BODEGA", label: "BODEGA" },
      { value: "OFICINA", label: "OFICINA" },
      { value: "OTRO", label: "OTRO" },
    ];
    return (
      <Modal
        open={addressModalOpen}
        onClose={() => !addressSaving && setAddressModalOpen(false)}
        size="lg"
        title={addressModalMode === "create" ? t("Agregar Dirección de Envío") : t("Editar Dirección de Envío")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddressModalOpen(false)} disabled={addressSaving}>
              {t("Cancelar")}
            </Button>
            <Button onClick={handleSaveAddress} loading={addressSaving} iconLeft={!addressSaving && <Save className="w-4 h-4" />}>
              {t("Guardar")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t("Dirección *")} htmlFor="addr-address" error={addressFormErrors.address}>
            <Input
              id="addr-address"
              value={addressFormData.address}
              onChange={(e) => handleAddressFieldChange("address", e.target.value)}
              invalid={!!addressFormErrors.address}
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Select
              label={t("Región *")}
              value={addressFormData.state}
              onChange={(e) => handleAddressRegionChange(e.target.value)}
              disabled={addressLoadingRegions}
              error={addressFormErrors.state || undefined}
              placeholder={addressLoadingRegions ? t("Cargando regiones...") : t("Seleccione una región")}
              options={addressRegions}
            />
            <Select
              label={t("Comuna *")}
              value={addressFormData.municipality}
              onChange={(e) => handleAddressFieldChange("municipality", e.target.value)}
              disabled={!addressFormData.state || addressLoadingCommunes}
              error={addressFormErrors.municipality || undefined}
              placeholder={addressLoadingCommunes ? t("Cargando comunas...") : t("Seleccione una comuna")}
              options={addressCommunes}
            />
            <Select
              label={t("Tipo *")}
              value={addressFormData.type}
              onChange={(e) => handleAddressFieldChange("type", e.target.value)}
              error={addressFormErrors.type || undefined}
              options={typeOptions}
            />
          </div>
          <Field label={t("Teléfono de Contacto *")} htmlFor="addr-phone" error={addressFormErrors.contactPhone} className="sm:max-w-xs">
            <Input
              id="addr-phone"
              value={addressFormData.contactPhone}
              onChange={(e) => handleAddressFieldChange("contactPhone", e.target.value)}
              invalid={!!addressFormErrors.contactPhone}
            />
          </Field>
        </div>
      </Modal>
    );
  }

  // Modal de bloqueo/desbloqueo por no pago. Extraído para usarse en lista y
  // detalle. `isBlockTargetBlocked` se computa local (evita TDZ en el detalle).
  function paymentBlockModal() {
    const isBlockTargetBlocked = paymentBlockTarget?.paymentBlock?.isBlocked;
    return (
      <Modal
        open={paymentBlockModalOpen}
        onClose={() => !paymentBlockSaving && setPaymentBlockModalOpen(false)}
        size="md"
        title={isBlockTargetBlocked ? t("Confirmar Desbloqueo") : t("Confirmar Bloqueo por No Pago")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPaymentBlockModalOpen(false)} disabled={paymentBlockSaving}>
              {t("Cancelar")}
            </Button>
            <Button
              variant={isBlockTargetBlocked ? "primary" : "danger"}
              onClick={handlePaymentBlockConfirm}
              loading={paymentBlockSaving}
              iconLeft={!paymentBlockSaving && (isBlockTargetBlocked ? <LockOpen className="w-4 h-4" /> : <Ban className="w-4 h-4" />)}
            >
              {isBlockTargetBlocked ? t("Confirmar Desbloqueo") : t("Confirmar Bloqueo")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3.5",
              isBlockTargetBlocked
                ? "border-status-success/30 bg-status-success-soft"
                : "border-status-warning/30 bg-status-warning-soft",
            )}
          >
            {isBlockTargetBlocked ? (
              <LockOpen className="w-5 h-5 text-status-success shrink-0 mt-0.5" aria-hidden="true" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-status-warning shrink-0 mt-0.5" aria-hidden="true" />
            )}
            <p className="text-sm text-fg leading-relaxed">
              {isBlockTargetBlocked ? (
                <>
                  {t("¿Desea")} <strong>{t("desbloquear")}</strong> {t("el acceso de todos los usuarios de")}{" "}
                  <strong>{paymentBlockTarget?.companyId?.name || t("esta empresa")}</strong>?{" "}
                  {t("Los usuarios podrán volver a iniciar sesión normalmente.")}
                </>
              ) : (
                <>
                  {t("Esta acción")} <strong>{t("bloqueará el acceso")}</strong> {t("de todos los usuarios de")}{" "}
                  <strong>{paymentBlockTarget?.companyId?.name || t("esta empresa")}</strong>.{" "}
                  {t("Los usuarios actualmente conectados serán")} <strong>{t("desconectados inmediatamente")}</strong>.
                </>
              )}
            </p>
          </div>
          <Field label={t("Motivo (opcional)")} htmlFor="payment-block-reason">
            <textarea
              id="payment-block-reason"
              rows={3}
              value={paymentBlockReason}
              onChange={(e) => setPaymentBlockReason(e.target.value)}
              disabled={paymentBlockSaving}
              placeholder={isBlockTargetBlocked ? t("Ej: Pago regularizado") : t("Ej: Factura vencida hace 30 días")}
              className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50 resize-y"
            />
          </Field>
        </div>
      </Modal>
    );
  }

  // En modo cartera la lista no depende de la paginación sino de la cartera
  // completa, así que el esqueleto sigue a ESE fetch.
  const isInitialLoading = clientSideMode
    ? carteraCompliance.loading
    : loading && contracts.length === 0;

  // Conteo de bloqueados sobre la CARTERA COMPLETA (ver arriba).
  const blockedCount = blockedCartera.length;
  const visibleContracts = clientSideMode
    ? carteraFiltered.slice(0, clientVisibleCount)
    : contracts;
  const clientHasMore = clientSideMode && carteraFiltered.length > clientVisibleCount;

  const STATUS_CARDS = [
    { value: "", label: t("Todos"), icon: Building2, tone: "brand" },
    { value: "active", label: t("Activos"), icon: CheckCircle2, tone: "success" },
    { value: "inactive", label: t("Inactivos"), icon: XCircle, tone: "neutral" },
    { value: "pending", label: t("Pendientes"), icon: Clock, tone: "warning" },
  ];

  return (
    <div className="w-full min-w-0 space-y-4 anim-fade-in-up">
      <PageHeader
        icon={<Building2 className="w-6 h-6 text-fg-muted" />}
        title={t("Empresas")}
        subtitle={t("Clientes B2B · contratos y cuentas")}
        actions={
          <div ref={addMenuRef} className="relative">
            <Button
              onClick={() => setAddMenuOpen((v) => !v)}
              iconLeft={<Plus className="w-4 h-4" />}
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
            >
              {t("Sumar empresa")}
            </Button>
            {addMenuOpen && (
              <div
                role="menu"
                aria-label={t("Sumar empresa")}
                className="absolute right-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-bg p-1 shadow-card"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-start gap-2.5 rounded-md px-3 py-2 text-left text-sm text-fg transition-colors hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  onClick={() => {
                    setAddMenuOpen(false);
                    setProspectFormOpen(true);
                  }}
                >
                  <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block font-medium">{t("Como prospecto")}</span>
                    <span className="block text-xs text-fg-muted">{t("Entra al pipeline comercial")}</span>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-start gap-2.5 rounded-md px-3 py-2 text-left text-sm text-fg transition-colors hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  onClick={() => {
                    setAddMenuOpen(false);
                    setCreateWizardOpen(true);
                  }}
                >
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block font-medium">{t("Como cliente")}</span>
                    <span className="block text-xs text-fg-muted">{t("Alta directa con contrato")}</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        }
      />

      {/* Subnav del hub: Directorio | Cuentas */}
      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList ariaLabel={t("Secciones de empresas")}>
          <TabsTrigger value="directorio" icon={<Building2 />}>
            {t("Directorio")}
            <span className="ml-1 inline-flex min-w-[1.25rem] justify-center rounded-full bg-bg-muted px-1.5 text-xs font-semibold tabular-nums text-fg-muted">
              {statusCounts.all}
            </span>
          </TabsTrigger>
          <TabsTrigger value="cuentas" icon={<Users />}>
            {t("Cuentas")}
          </TabsTrigger>
          <TabsTrigger value="holdings" icon={<Network />}>
            {t("Holdings")}
          </TabsTrigger>
        </TabsList>

        {/* ── DIRECTORIO ── datos reales presentados como grid de tarjetas ── */}
        <TabsContent value="directorio" className="mt-4 space-y-4">
          {/* Fila de métricas: KPIs clicables (filtro server-side) */}
          <KpiFilterCards
            className="sm:!grid-cols-2 lg:!grid-cols-4"
            cards={STATUS_CARDS}
            counts={{ active: statusCounts.active, inactive: statusCounts.inactive, pending: statusCounts.pending }}
            total={statusCounts.all}
            active={statusFilter}
            onChange={setStatusFilter}
          />

          {/* Pedidos últimos 30 días: conteo real (client-volume, con alcance del filtro).
              Carpeta al día: semáforo de cumplimiento front-only sobre la cartera completa. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <Stat
              className="sm:w-72"
              label={t("Pedidos últimos 30 días")}
              value={orders30dStat.value}
              icon={<Package />}
              tone="brand"
              hint={orders30dStat.hint}
            />
            <Stat
              className="sm:w-72"
              label={t("Carpeta al día")}
              value={carteraCompliance.loading ? "…" : `${cartera.alDia}/${cartera.total}`}
              icon={<ShieldCheck />}
              tone="success"
              hint={t("Cumplimiento front-only · cartera completa")}
            />
          </div>

          {/* Toolbar: búsqueda por nombre, razón social o RUT + chip bloqueados */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" aria-hidden="true" />
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("Buscar por nombre, razón social o RUT...")}
                aria-label={t("Buscar empresas por nombre, razón social o RUT")}
                // `!` obligatorio: el primitivo Input trae `px-3` y twMerge NO
                // lo descarta (su mapa de conflictos solo va de `px`→`pl/pr`,
                // no al revés), así que sobrevive en el class list y Bootstrap
                // lo pinta con `!important` → el placeholder quedaba debajo de
                // la lupa ("🔍uscar por identificador…").
                className="!pl-9 !pr-9"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-fg-muted hover:text-fg hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  aria-label={t("Limpiar búsqueda")}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              type="button"
              aria-pressed={onlyBlocked}
              onClick={() => setOnlyBlocked((v) => !v)}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg border px-3 h-10 text-sm font-medium transition-colors",
                onlyBlocked
                  ? "border-status-error/40 bg-status-error-soft text-status-error"
                  : "border-border bg-bg text-fg-muted hover:text-fg",
              )}
              title={t("Mostrar solo las empresas con bloqueo de pago (cartera completa)")}
            >
              <Ban className="w-4 h-4" />
              {t("Bloqueados")}
              <span
                className={cn(
                  "inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums",
                  onlyBlocked ? "bg-status-error/20" : "bg-bg-muted text-fg-muted",
                )}
              >
                {carteraCompliance.loading ? "…" : blockedCount}
              </span>
            </button>

            {/* Vigencia y orden operan sobre la cartera completa (client-side). */}
            <Select
              aria-label={t("Filtrar por vigencia del contrato")}
              value={vigenciaFilter}
              onChange={(e) => setVigenciaFilter(e.target.value)}
              className="w-44"
              options={[
                { value: "", label: t("Toda vigencia") },
                { value: "vigente", label: t("Vigente") },
                { value: "por_vencer", label: t("Por vencer (≤30 días)") },
                { value: "terminada", label: t("Terminada") },
                { value: "indefinida", label: t("Indefinida") },
              ]}
            />
            <SortControl
              ariaLabel={t("Ordenar empresas")}
              field={sortField}
              dir={sortDir}
              onFieldChange={setSortField}
              onDirChange={setSortDir}
              // w-64: "Por cantidad de pedidos (30 días)" no entra en el w-56
              // que usaban las opciones viejas.
              selectClassName="w-64"
              buttonClassName="h-10 w-10"
              fields={[
                { value: "orders", label: t("Por cantidad de pedidos (30 días)"), defaultDir: "desc" },
                { value: "name", label: t("Por nombre"), defaultDir: "asc" },
                { value: "expiry", label: t("Por vencimiento"), defaultDir: "asc" },
              ]}
            />
          </div>

          {/* Contenido */}
      {clientSideMode && carteraCompliance.truncado && (
        <p className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-fg-muted">
          {t("La cartera supera el tope de carga: el orden, los filtros y la suma consideran solo los contratos cargados.")}
        </p>
      )}
      {clientSideMode && carteraCompliance.error && !carteraCompliance.loading ? (
        <div className="rounded-lg border border-border bg-bg">
          <EmptyState
            icon={<AlertTriangle />}
            title={t("No se pudo cargar la cartera completa")}
            description={t("El orden y los filtros de vigencia/bloqueo necesitan la cartera completa. Quita esos filtros para volver a la lista paginada, o reintenta más tarde.")}
            action={
              <Button
                variant="outline"
                onClick={() => {
                  setSortField("");
                  setVigenciaFilter("");
                  setOnlyBlocked(false);
                }}
              >
                {t("Quitar orden y filtros")}
              </Button>
            }
          />
        </div>
      ) : isInitialLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border bg-bg p-[1.25rem] space-y-4" aria-hidden="true">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-full bg-bg-subtle animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-2/3 rounded bg-bg-subtle animate-pulse" />
                  <div className="h-3 w-1/2 rounded bg-bg-subtle animate-pulse" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-bg-subtle animate-pulse" />
                <div className="h-3 w-4/5 rounded bg-bg-subtle animate-pulse" />
              </div>
              <div className="border-t border-border pt-3">
                <div className="h-6 w-28 rounded-full bg-bg-subtle animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : visibleContracts.length === 0 ? (
        <div className="rounded-lg border border-border bg-bg">
          <EmptyState
            icon={onlyBlocked ? <Ban /> : <Building2 />}
            title={
              onlyBlocked
                ? t("No hay empresas bloqueadas por no pago")
                : vigenciaFilter
                ? t("No hay contratos con esa vigencia")
                : searchTerm
                ? t("No se encontraron empresas con esa búsqueda")
                : statusFilter
                ? t("No hay contratos con ese estado")
                : t("No hay contratos disponibles")
            }
            description={
              onlyBlocked
                ? t("Ninguna empresa de la cartera coincide con el filtro de bloqueo por no pago.")
                : vigenciaFilter
                ? t("Ningún contrato de la cartera coincide con esa vigencia. Prueba con otra o quita el filtro.")
                : searchTerm
                ? t("Revisa lo que escribiste o limpia la búsqueda para ver todas las empresas.")
                : statusFilter
                ? t("Prueba con otro estado o quita el filtro.")
                : t("Crea tu primer contrato para empezar a operar con tus clientes.")
            }
            action={
              onlyBlocked ? (
                <Button variant="outline" onClick={() => setOnlyBlocked(false)}>
                  {t("Ver todos")}
                </Button>
              ) : vigenciaFilter ? (
                <Button variant="outline" onClick={() => setVigenciaFilter("")}>
                  {t("Quitar filtro de vigencia")}
                </Button>
              ) : searchTerm ? (
                <Button variant="outline" onClick={clearSearch}>
                  {t("Limpiar búsqueda")}
                </Button>
              ) : (
                <Button onClick={() => setCreateWizardOpen(true)} iconLeft={<Plus className="w-4 h-4" />}>
                  {t("Crear Nuevo Contrato")}
                </Button>
              )
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibleContracts.map((contract) => (
            <EmpresaCard key={contract._id} contract={contract} onView={handleViewClick} />
          ))}
        </div>
      )}

      {/* Scroll infinito — solo para la lista paginada. En modo cartera
          (bloqueados / vigencia / orden) la vista sale de la cartera completa
          ya fetcheada, con ventana de "Mostrar más". */}
      {!isInitialLoading && !clientSideMode && contracts.length > 0 && hasMore && (
        <div ref={observerTarget} className="flex items-center justify-center py-6" aria-hidden="true">
          {loadingMore && (
            <span
              className="inline-block w-6 h-6 border-2 border-border border-t-brand-500 rounded-full animate-spin"
              role="status"
              aria-label={t("Cargando más contratos...")}
            />
          )}
        </div>
      )}

          {clientHasMore && (
            <div className="flex justify-center py-4">
              <Button variant="outline" onClick={() => setClientVisibleCount((c) => c + PAGE_CLIENT)}>
                {t("Mostrar más")}
                <span className="ml-1.5 tabular-nums text-fg-muted">
                  ({carteraFiltered.length - clientVisibleCount})
                </span>
              </Button>
            </div>
          )}

          {!isInitialLoading && clientSideMode && visibleContracts.length > 0 && !clientHasMore && (
            <p className="text-center text-sm text-fg-muted py-4" role="status" aria-live="polite">
              {t("Mostrando la cartera filtrada completa")} ({carteraFiltered.length})
            </p>
          )}

          {!isInitialLoading && !clientSideMode && contracts.length > 0 && !hasMore && (
            <p className="text-center text-sm text-fg-muted py-4" role="status" aria-live="polite">
              {t("Se han mostrado todos los contratos")} ({contracts.length})
            </p>
          )}
        </TabsContent>

        {/* ── CUENTAS ── administración global de los usuarios de comercios
            (ex /admin/users?tab=clientes, que redirige acá). `contracts` solo
            para enriquecer el RUT de la empresa en el detalle (best-effort). ── */}
        <TabsContent value="cuentas" className="mt-4">
          <CuentasUsuariosPanel contracts={contracts} />
        </TabsContent>

        {/* ── HOLDINGS ── holdings del operador y su ficha de cobro (datos fiscales,
            contacto y plazo, tarifa y empresas). ── */}
        <TabsContent value="holdings" className="mt-4">
          <HoldingsPanel />
        </TabsContent>
      </Tabs>

      {addressModal()}
      {paymentBlockModal()}

      {/* Alta rápida de prospecto ("Sumar empresa → Como prospecto"). */}
      <ProspectFormModal
        open={prospectFormOpen}
        editing={null}
        submitting={prospectSubmitting}
        onClose={() => setProspectFormOpen(false)}
        onSubmit={handleProspectSubmit}
      />
    </div>
  );
};

export default ContractsAdmin;
